import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import firebase from 'firebase/compat';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom, Observable } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { PeriodBarResult } from '../../../ui/components/period-bar/period-bar.component';
import { SharedDocumentId } from '../../document/domain/shared_document/shared_document';
import { PatientId } from '../../patient/domain/patient';
import { Bill, BillStatus, PaymentMethod } from '../domain/bill/bill';
import { BillNotFoundException } from '../domain/bill/bill-exceptions';
import { BillLine } from '../domain/bill/bill-line';
import {
  BillLineDescription,
  BillLineDescriptionProps,
} from '../domain/bill/bill-line-description';
import { Credit, CreditId } from '../domain/credit/credit';
import { Dietitian, DietitianId } from '../domain/dietitian';
import { EstimateId } from '../domain/estimate/estimate';
import { InnerThirdPartySchema } from './estimate.repository';
import { ThirdPartyRepository } from './third-party.repository';
import Query = firebase.firestore.Query;

export interface BillLineSchema {
  billId: string;
  descriptionId: string;
  comment: string | null;
  amount: number;
  date: string | null;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export interface BillSchema {
  patientId: string | null;
  patientFullname: string | null;
  thirdParty: InnerThirdPartySchema | null;
  referencePrefix: string | null;
  reference: number | null;
  billedAt: Timestamp | null;
  lines: BillLineSchema[];
  amountExclTax: number;
  amountInclTax: number;
  amountTax: number;
  rateTax: number | null;
  withTax: boolean;
  draftedAt: Timestamp | null;
  emittedAt: Timestamp | null;
  paidAt: Timestamp | null;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  archived: boolean;
  archivedAt: Timestamp | null;
  status: BillStatus;
  url: string | null;
  path: string | null;
  documentHasReady: boolean;
  paymentMethod: PaymentMethod | null;
  estimateId: string | null;
  sharedDocumentId: string | undefined;
  sharedAt: Timestamp | undefined;
  creditId: string | null;
  eInvoiceStatus: string | null;
  eInvoiceId: string | null;
}

export interface ResultCalcTot {
  count: number;
  amountIncludingTax: number;
  amountExcludingTax: number;
}

@Injectable()
export class BillRepository {
  private readonly calcTotalBill: (data: unknown) => Observable<ResultCalcTot>;
  private readonly previewPdf: (data: unknown) => Observable<string>;

  constructor(
    private firestore: AngularFirestore,
    private functions: AngularFireFunctions,
  ) {
    this.calcTotalBill = this.functions.httpsCallable<unknown, ResultCalcTot>(
      'bill-calcTotalBill',
    );
    this.previewPdf = this.functions.httpsCallable<unknown, string>(
      'bill-previewPdf',
    );
  }

  private collection(dietitianId: string, queryFn?: QueryFn) {
    return this.firestore
      .collection('dietitians')
      .doc(dietitianId)
      .collection<BillSchema>('bills', queryFn);
  }

  public toSchema(bill: Bill): BillSchema {
    return {
      patientId: bill.patientId?.id.toString() ?? null,
      patientFullname: bill.patientFullname ?? null,
      thirdParty: bill.thirdParty
        ? ThirdPartyRepository.innerThirdPartySchemaToThirdParty(
            bill.thirdParty,
          )
        : null,
      referencePrefix: bill.referencePrefix ?? null,
      reference: bill.reference ?? null,
      billedAt: bill.billedAt ? Timestamp.fromDate(bill.billedAt) : null,
      lines: this.toLineSchema(bill),
      amountExclTax: bill.amountExclTax,
      amountInclTax: bill.amountInclTax,
      amountTax: bill.amountTax,
      rateTax: bill.rateTax ?? null,
      withTax: bill.withTax,
      draftedAt:
        bill.draftedAt !== undefined
          ? Timestamp.fromDate(bill.draftedAt)
          : Timestamp.now(),
      emittedAt:
        bill.emittedAt !== undefined
          ? Timestamp.fromDate(bill.emittedAt)
          : null,
      paidAt:
        bill.paidAt !== undefined ? Timestamp.fromDate(bill.paidAt) : null,
      createdAt:
        bill.createdAt !== undefined
          ? Timestamp.fromDate(bill.createdAt)
          : Timestamp.now(),
      updatedAt: Timestamp.now(),
      archived: bill.archived,
      archivedAt:
        bill.archivedAt !== undefined
          ? Timestamp.fromDate(bill.archivedAt)
          : null,
      status: bill.status,
      url: bill.url ?? null,
      path: bill.path ?? null,
      documentHasReady: bill.documentHasReady,
      paymentMethod: bill.paymentMethod ?? null,
      estimateId: bill.estimateId?.id.toString() ?? null,
      sharedDocumentId: bill.sharedDocumentId?.id.toString() ?? null,
      sharedAt:
        bill.sharedAt !== undefined ? Timestamp.fromDate(bill.sharedAt) : null,
      creditId: bill.creditId?.id.toString() ?? null,
      eInvoiceStatus: bill.eInvoiceStatus ?? null,
      eInvoiceId: bill.eInvoiceId ?? null,
    } as BillSchema;
  }

  private toLineSchema(bill: Bill): BillLineSchema[] {
    return bill.lines.map((line) => {
      return {
        billId: bill.id.toString(),
        descriptionId: line.description?.id.toString(),
        comment: line.comment ?? null,
        amount: line.amount,
        date: line.date ?? null,
        createdAt:
          line.createdAt !== undefined
            ? Timestamp.fromDate(line.createdAt)
            : Timestamp.now(),
        updatedAt: Timestamp.now(),
      } as BillLineSchema;
    });
  }

  fromSchema(schema: BillSchema, dietitianId: string, id?: string): Bill {
    return Bill.create(
      {
        dietitianId: DietitianId.create(new UniqueEntityID(dietitianId)),
        patientId: schema.patientId
          ? PatientId.create(new UniqueEntityID(schema.patientId))
          : undefined,
        thirdParty: schema.thirdParty
          ? ThirdPartyRepository.thirdPartyFormInnerThirdPartySchema(
              schema.thirdParty,
            )
          : undefined,
        patientFullname: schema.patientFullname ?? undefined,
        referencePrefix: schema.referencePrefix ?? undefined,
        reference: schema.reference ?? undefined,
        billedAt: schema.billedAt?.toDate() ?? undefined,
        lines: this.fromLineSchema(schema.lines),
        amountExclTax: schema.amountExclTax,
        amountInclTax: schema.amountInclTax,
        amountTax: schema.amountTax,
        rateTax: schema.rateTax ?? undefined,
        withTax: schema.withTax,
        draftedAt: schema.draftedAt?.toDate() ?? undefined,
        emittedAt: schema.emittedAt?.toDate() ?? undefined,
        paidAt: schema.emittedAt?.toDate() ?? undefined,
        archived: schema.archived,
        archivedAt: schema.emittedAt ? schema.emittedAt?.toDate() : undefined,
        createdAt: schema.createdAt?.toDate() ?? undefined,
        updatedAt: schema.updatedAt?.toDate() ?? undefined,
        status: schema.status,
        url: schema.url ?? undefined,
        path: schema.path ?? undefined,
        documentHasReady: schema.documentHasReady,
        paymentMethod: schema.paymentMethod ?? undefined,
        estimateId: schema.estimateId
          ? EstimateId.create(new UniqueEntityID(schema.estimateId))
          : undefined,
        sharedDocumentId: schema.sharedDocumentId
          ? SharedDocumentId.create(new UniqueEntityID(schema.sharedDocumentId))
          : undefined,
        sharedAt: schema.sharedAt?.toDate() ?? undefined,
        creditId: schema.creditId
          ? CreditId.create(new UniqueEntityID(schema.creditId))
          : undefined,
        eInvoiceStatus: schema.eInvoiceStatus ?? undefined,
        eInvoiceId: schema.eInvoiceId ?? undefined,
      },
      new UniqueEntityID(id),
    );
  }

  private fromLineSchema(lines: BillLineSchema[]): BillLine[] {
    return lines.map((line) => {
      return BillLine.create({
        description: BillLineDescription.create(
          {} as BillLineDescriptionProps,
          new UniqueEntityID(line.descriptionId),
        ),
        comment: line.comment ?? undefined,
        amount: line.amount,
        date: line.date ?? undefined,
        createdAt: line.createdAt?.toDate() ?? undefined,
        updatedAt: line.updatedAt?.toDate() ?? undefined,
      });
    });
  }

  async create(bill: Bill): Promise<Bill> {
    const schema = this.toSchema(bill);
    if (bill.dietitianId) {
      if (schema.status !== 'DRAFT') {
        // Reference
        const now = new Date();
        schema.referencePrefix =
          now.getFullYear().toString() +
          (now.getMonth() + 1 < 10
            ? '0' + (now.getMonth() + 1)
            : now.getMonth() + 1);
        const lastReference = await this.findLastReferenceByReferencePrefix(
          bill.dietitianId.id.toString(),
          schema.referencePrefix,
        );
        if (lastReference && lastReference.length === 1) {
          schema.reference = lastReference[0].reference
            ? lastReference[0].reference + 1
            : null;
        } else {
          schema.reference = 1;
        }
      } else {
        schema.referencePrefix = null;
        schema.reference = null;
      }

      // Calcul des totaux
      const taxFactor = schema.withTax ? Number(schema.rateTax) / 100 : 0;
      for (const line of schema.lines) {
        schema.amountExclTax += Number(line.amount);
        schema.amountTax += Number(line.amount) * taxFactor;
        schema.amountInclTax +=
          Number(line.amount) + Number(line.amount) * taxFactor;
      }

      const ref = await this.collection(bill.dietitianId.id.toString()).add(
        schema,
      );
      return this.fromSchema(schema, bill.dietitianId.id.toString(), ref.id);
    } else {
      return Promise.reject('Diététicien non identifié');
    }
  }

  async save(bill: Bill): Promise<Bill> {
    const schema = this.toSchema(bill);
    if (bill.dietitianId) {
      if (schema.status === 'EMITTED' || schema.status === 'PAID') {
        // Reference
        if (!schema.referencePrefix && !schema.reference) {
          const now = new Date();
          schema.referencePrefix =
            now.getFullYear().toString() +
            (now.getMonth() + 1 < 10
              ? '0' + (now.getMonth() + 1)
              : now.getMonth() + 1);
          const lastReference = await this.findLastReferenceByReferencePrefix(
            bill.dietitianId.id.toString(),
            schema.referencePrefix,
          );
          if (lastReference && lastReference.length === 1) {
            schema.reference = lastReference[0].reference
              ? lastReference[0].reference + 1
              : null;
          } else {
            schema.reference = 1;
          }
        }
        // Calcul des totaux
        schema.amountExclTax = 0;
        schema.amountTax = 0;
        schema.amountInclTax = 0;
        const taxFactor = schema.withTax ? Number(schema.rateTax) / 100 : 0;
        for (const line of schema.lines) {
          schema.amountExclTax += Number(line.amount);
          schema.amountTax += Number(line.amount) * taxFactor;
          schema.amountInclTax +=
            Number(line.amount) + Number(line.amount) * taxFactor;
        }
      }

      const dietitianId = bill.dietitianId.id.toString();
      await this.collection(dietitianId)
        .doc(bill.billId.id.toString())
        .set(schema);
      return this.fromSchema(schema, dietitianId, bill.billId.id.toString());
    } else {
      return Promise.reject('Diététicien non identifié');
    }
  }

  async load(dietitianId: string, billId: string): Promise<Bill> {
    const snap = await firstValueFrom(
      this.collection(dietitianId).doc(billId).get(),
    );
    if (!snap.exists || snap.data() == null) {
      throw new BillNotFoundException();
    }
    const schema = snap.data() as BillSchema;
    return this.fromSchema(schema, dietitianId, snap.id);
  }

  async getMinBilledDate(dietitianId: string): Promise<Date | undefined> {
    try {
      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) =>
          ref
            .where('status', '!=', 'CANCELLED')
            .orderBy('status', 'asc')
            .orderBy('billedAt', 'desc')
            .limit(1),
        ).get(),
      );
      if (snap.size > 0) {
        return snap.docs[0].data().billedAt?.toDate();
      }
    } catch (e) {
      console.error('🔴 getMinBilledDate', e);
    }
    return undefined;
  }

  private async findLastReferenceByReferencePrefix(
    dietitianId: string,
    referencePrefix: string | undefined,
  ) {
    let snap;
    if (referencePrefix) {
      snap = await firstValueFrom(
        this.collection(dietitianId, (ref) =>
          ref
            .where('referencePrefix', '==', referencePrefix)
            .orderBy('reference', 'desc')
            .limit(1),
        ).get(),
      );
    } else {
      snap = await firstValueFrom(
        this.collection(dietitianId, (ref) =>
          ref.orderBy('reference', 'desc').limit(1),
        ).get(),
      );
    }
    return snap.docs.map((doc) =>
      this.fromSchema(doc.data(), dietitianId, doc.id),
    );
  }

  async findAllForPatient(
    dietitianId: string,
    patientId: string,
  ): Promise<Bill[]> {
    const snap = await firstValueFrom(
      this.collection(dietitianId, (ref) =>
        ref.where('patientId', '==', patientId),
      ).get(),
    );
    return snap.docs.map((doc) =>
      this.fromSchema(doc.data(), dietitianId, doc.id),
    );
  }

  async findAllExported(
    dietitianId: string,
    period: PeriodBarResult | undefined,
    status: BillStatus | undefined,
    isArchived: boolean,
  ): Promise<Bill[]> {
    const snap = await firstValueFrom(
      this.collection(dietitianId, (ref) => {
        let qry = ref.where('archived', '==', isArchived);
        if (status) {
          qry = qry.where('status', '==', status);
        }
        if (period) {
          if (period.start) {
            qry = qry.where('billedAt', '>=', period.start.toDate());
          }
          if (period.end) {
            qry = qry.where('billedAt', '<=', period.end.toDate());
          }
          if (period.start || period.end) {
            qry = qry.orderBy('billedAt', 'desc');
          }
        }
        qry = qry.orderBy('updatedAt', 'desc');
        return qry;
      }).get(),
    );
    return snap.docs.map((doc) =>
      this.fromSchema(doc.data() as BillSchema, dietitianId, doc.id),
    );
  }

  async findAllActivated(
    dietitianId: string,
    period: PeriodBarResult | undefined,
    status: BillStatus | undefined,
    orderBy: string,
    lastId?: string | undefined,
  ): Promise<Bill[]> {
    if (lastId) {
      const last = await firstValueFrom(
        this.collection(dietitianId).doc(lastId).get(),
      );

      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) => {
          // Archived
          let qry = ref.where('archived', '==', false);

          // Filter
          qry = this.doFilter(period, status, qry);

          // OrderBy
          qry = this.doOrderBy(period, orderBy, qry);

          return qry.startAfter(last).limit(30);
        }).get(),
      );
      return snap.docs.map((doc) =>
        this.fromSchema(doc.data(), dietitianId, doc.id),
      );
    } else {
      try {
        const snap = await firstValueFrom(
          this.collection(dietitianId, (ref) => {
            // Archived
            let qry = ref.where('archived', '==', false);

            // Filter
            qry = this.doFilter(period, status, qry);

            // OrderBy
            qry = this.doOrderBy(period, orderBy, qry);

            return qry.limit(30);
          }).get(),
        );
        return snap.docs.map((doc) =>
          this.fromSchema(doc.data(), dietitianId, doc.id),
        );
      } catch (e) {
        console.log('❌', e);
        return [];
      }
    }
  }

  private doFilter(
    period: PeriodBarResult | undefined,
    status: BillStatus | undefined,
    qry: Query,
  ): Query {
    if (status) {
      qry = qry.where('status', '==', status);
    }
    if (period) {
      if (period.start) {
        qry = qry.where('billedAt', '>=', period.start.toDate());
      }
      if (period.end) {
        qry = qry.where('billedAt', '<=', period.end.toDate());
      }
    }
    return qry;
  }

  private doOrderBy(
    period: PeriodBarResult | undefined,
    orderBy: string,
    qry: Query,
  ): Query {
    if (orderBy === 'ref@desc') {
      if (period && (period.start || period.end)) {
        qry = qry.orderBy('billedAt', 'desc');
      }
      qry = qry.orderBy('referencePrefix', 'desc').orderBy('reference', 'desc');
    } else if (orderBy === 'ref@asc') {
      if (period && (period.start || period.end)) {
        qry = qry.orderBy('billedAt', 'desc');
      }
      qry = qry.orderBy('referencePrefix', 'asc').orderBy('reference', 'asc');
    } else if (orderBy === 'billedAt@desc') {
      qry = qry.orderBy('billedAt', 'desc');
    } else if (orderBy === 'billedAt@asc') {
      qry = qry.orderBy('billedAt', 'asc');
    } else if (orderBy === 'amountExclTax@desc') {
      if (period && (period.start || period.end)) {
        qry = qry.orderBy('billedAt', 'desc');
      }
      qry = qry.orderBy('amountExclTax', 'desc');
    } else if (orderBy === 'amountExclTax@asc') {
      if (period && (period.start || period.end)) {
        qry = qry.orderBy('billedAt', 'desc');
      }
      qry = qry.orderBy('amountExclTax', 'asc');
    }
    return qry;
  }

  async findAllArchived(
    dietitianId: string,
    period: PeriodBarResult | undefined,
    lastId: string | undefined,
  ) {
    if (lastId) {
      const last = await firstValueFrom(
        this.collection(dietitianId).doc(lastId).get(),
      );
      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) => {
          let qry = ref.where('archived', '==', true);
          if (period) {
            if (period.start) {
              qry = qry.where('billedAt', '>=', period.start.toDate());
            }
            if (period.end) {
              qry = qry.where('billedAt', '<=', period.end.toDate());
            }
          }
          qry = qry.orderBy('updatedAt', 'desc').startAfter(last).limit(30);
          return qry;
        }).get(),
      );
      return snap.docs.map((doc) =>
        this.fromSchema(doc.data(), dietitianId, doc.id),
      );
    } else {
      try {
        const snap = await firstValueFrom(
          this.collection(dietitianId, (ref) => {
            let qry = ref.where('archived', '==', true);
            if (period) {
              if (period.start) {
                qry = qry.where('billedAt', '>=', period.start.toDate());
              }
              if (period.end) {
                qry = qry.where('billedAt', '<=', period.end.toDate());
              }
              if (period.start || period.end) {
                qry = qry.orderBy('billedAt', 'desc');
              }
            }
            qry = qry.orderBy('updatedAt', 'desc').limit(30);
            return qry;
          }).get(),
        );
        return snap.docs.map((doc) =>
          this.fromSchema(doc.data(), dietitianId, doc.id),
        );
      } catch (e) {
        console.log('❌', e);
        return [];
      }
    }
  }

  async calcTotalBillFromCloud(
    archived: boolean,
    start: Date | undefined,
    end: Date | undefined,
    status: BillStatus | undefined,
  ): Promise<ResultCalcTot> {
    const data = {
      archived,
      start,
      end,
      status,
    };
    return firstValueFrom(this.calcTotalBill(data));
  }

  async previewPdfFromCloud(): Promise<string> {
    return firstValueFrom(this.previewPdf({}));
  }

  async delete(dietitian: Dietitian, bill: Bill): Promise<void> {
    if (bill.status === BillStatus.DRAFT) {
      return this.collection(dietitian.id.toString())
        .doc(bill.id.toString())
        .delete();
    }
  }

  async cancel(bill: Bill, credit: Credit): Promise<Bill | undefined> {
    if (bill.status === BillStatus.CANCELLED) {
      bill.creditId = credit.creditId;
      return this.save(bill);
    }
    return Promise.resolve(undefined);
  }

  billValueChanges(
    dietitianId: string,
    billId: string,
  ): Observable<BillSchema | undefined> {
    return this.collection(dietitianId).doc(billId).valueChanges();
  }
}
